import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { helperService, IDogPortrait } from "../../../services/helper.service";
import { Helmet } from "react-helmet";

import { ClubRegistrationDto } from "@eagerdog/interfaces";

import "../../../styles/Signup.scss";

import logo from "../../../assets/logo.svg";
import clubIcon from "../../../assets/icons/club.svg";
import keyIcon from "../../../assets/icons/key.svg";
import flagIcon from "../../../assets/icons/flag.svg";
import pinIcon from "../../../assets/icons/pin.svg";
import emailIcon from "../../../assets/icons/email.svg";

import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import Dropdown, { Provinces, States, IOption } from "../../../components/Dropdown/Dropdown";
import Phone from "../../../components/Phone/Phone";
import { toast } from "../../../components/Toast/ToastManager";

//import dog2 from "../../../assets/ren_450x450.png";

import { apiService } from "../../../services";

interface IProps {}

const KennelClub: React.FC<IProps> = (props) => {
  const navigate = useNavigate();

  const [step, setStep] = useState<number>(0);
  const [clubName, setClubName] = useState<string>("");
  const [clubCountry, setClubCountry] = useState<IOption>({ value: "USA", id: "USA" });
  const [clubProvince, setClubProvince] = useState<IOption>({ value: "", id: "" });
  const [clubState, setClubState] = useState<IOption>({ value: "", id: "" });
  const [clubEmail, setClubEmail] = useState<string>("");
  const [clubPhone, setClubPhone] = useState<string>("");

  const [countryError, setCountryError] = useState<boolean>(false);
  const [provinceError, setProvinceError] = useState<boolean>(false);
  const [stateError, setStateError] = useState<boolean>(false);

  const [password, setPassword] = useState<string>("");
  const [repeat, setRepeat] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const stepAmt:number = 3;

  useEffect(() => {
    if (helperService.isLoggedIn()) {
      navigate("/");
    }
  });

  const getStepText = () => {
    return (<span>{step + 1} / {stepAmt}</span>);
  }

  const passwordMatch = () => {
    if (password !== repeat) {
      toast.show({
        title: "Error",
        content: "The passwords you entered do not match",
        duration: 10000,
        type: "fail"
      });

      return false;
    }

    return true;
  }

  const register = () => {
      let payload:ClubRegistrationDto = {
        club_name: clubName,
        address: { country: clubCountry.value === "USA" ? "US" : "CA", state: clubCountry.value === "USA" ? clubState.id : clubProvince.id },
        email: clubEmail,
        phone: clubPhone,
        password: password
      };

      setIsLoading(true);

      apiService.registerClub(payload).then((response: any) => {
        if (response._id) {
          navigate('/verify-email?t=club');
        }
      }).catch((error: any) => {
          toast.show({
            title: "Error",
            content: error.response.data.message ? error.response.data.message : "Something went wrong, please try again later",
            duration: 10000,
            type: "fail"
          });
      }).finally(() => {
        setIsLoading(false);
      });
  }

  const validateForm = () => {
    let valid:boolean = true;
    let errorMsg:string = "Please select a country";

    setCountryError(false);
    setProvinceError(false);
    setStateError(false);

    if (clubCountry.value === "" ) {
      setCountryError(true);
      valid = false;
    }

    if (clubCountry.value === "Canada" && clubProvince.value === "") {
      errorMsg = "Please select a province";
      setProvinceError(true);
      valid = false;
    }

    if (clubCountry.value === "USA" && clubState.value === "") {
      errorMsg = "Please select a state";
      setStateError(true);
      valid = false;
    }

    if (!valid) {
      toast.show({
        title: "Error",
        content: errorMsg,
        duration: 10000,
        type: "fail"
      });
    }

    return valid;
  }

  return (
    <div className="KennelClub Signup">
      <Helmet>
        <title>Register - Kennel Club</title>
        <meta name="description" content="Register as a kennel club on Eager Dog." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <div className="images">
        <div className="logo"><Link to="/"><img src={logo} alt="Eager Dog" /></Link></div>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.2 }} className="dogCards">
          <div className="cardRow">
            {helperService.topDogs.map((dog:IDogPortrait, i:number) => {
              return(
                <div key={i} className="dogCard">
                  <div className="dogPortrait">
                    <img src={dog.image} alt={"Dog Portrait of " + dog.name} />
                  </div>
                  <span>{dog.name}</span>
                </div>
              );
            })}
          </div>
          <div className="cardRow">
            {helperService.bottomDogs.map((dog:IDogPortrait, i:number) => {
              return(
                <div key={i} className="dogCard">
                  <div className="dogPortrait">
                    <img src={dog.image} alt={"Dog Portrait of " + dog.name} />
                  </div>
                  <span>{dog.name}</span>
                </div>
              );
            })}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, translateY: "-300px" }} whileInView={{ opacity: 1, translateY: "-50px" }} transition={{ delay: 0.5 }} className="dogBanner">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 1 }} className="bannerInner">
            <div className="small">The Best Place</div>
            <div className="large">for your <span>Dog</span></div>
            <div className="dogWrap">
              <div className="portrait"><img src={helperService.allDogs[1].image} alt={"Dog Portrait of " + helperService.allDogs[1].name}/></div>
            </div>
            <div className="dogName">{helperService.allDogs[1].name}</div>
          </motion.div>
        </motion.div>
      </div>
      <div className="content">
        <div className="inner">
          <div className="logo"><img src={logo} alt="Eager Dog" /></div>
          <h1>Register</h1>
          <div className="stepWrap">
            <div className="steps" style={{ width: (100 * stepAmt) + "%", transform: "translateX(" + -((step * 100) / stepAmt) + "%)" }}>
              <div className="step" style={{ width: (100 / stepAmt) + "%" }}>
                <div className="stepInner">
                  <div className="form">
                    <form onSubmit={(e) => { e.preventDefault(); if (validateForm()) { setStep(step + 1); } }}>
                      <div className="stepTitle"><span>Your Information</span>{ getStepText() }</div>
                      <Input required onChange={(e) => { setClubName(e.target.value); }} type="text" icon={clubIcon} label="Kennel Club or Company Name" placeholder="Enter your kennel club or company name" />
                      <Dropdown error={countryError} value={clubCountry} onChange={(e: any, value: IOption) => { setClubCountry(value); }} icon={flagIcon} label="Country" options={[{ value: "Canada", id: "CA" }, { value: "USA", id: "US" }]} placeholder="Choose your country of residence" />
                      {clubCountry.value === "USA" && <Dropdown error={stateError} value={clubState} onChange={(e: any, value: IOption) => { setClubState(value); }} icon={pinIcon} label="State" options={States} placeholder="Choose your state" />}
                      {clubCountry.value === "Canada" && <Dropdown error={provinceError} value={clubProvince} onChange={(e: any, value: IOption) => { setClubProvince(value); }} icon={pinIcon} label="Province / State" options={Provinces} placeholder="Choose your province" />}
                      <div className="actions">
                        <button type="submit">Next</button>
                      </div>
                      <div className="register">Already have an account? <Link to="/login">Log in</Link></div>
                    </form>
                  </div>
                </div>
              </div>
              <div className="step" style={{ width: (100 / stepAmt) + "%" }}>
                <div className="stepInner">
                  <div className="form">
                    <form onSubmit={(e) => { e.preventDefault(); setStep(step + 1); }}>
                      <div className="stepTitle"><span>Your Information</span>{ getStepText() }</div>
                      <Input required onChange={(e) => { setClubEmail(e.target.value); }} type="email" icon={emailIcon} label="Email" placeholder="Enter your email" />
                      <Phone label="Contact Phone Number" value={clubPhone} onChange={setClubPhone} placeholder="Enter your phone number" />
                      <div className="actions two">
                        <button type="button" onClick={() => { setStep(step - 1); }}>Previous</button>
                        <button type="submit">Next</button>
                      </div>
                      <div className="register">Already have an account? <Link to="/login">Log in</Link></div>
                    </form>
                  </div>
                </div>
              </div>
              <div className="step" style={{ width: (100 / stepAmt) + "%" }}>
                <div className="stepInner">
                  <div className="form">
                    <form onSubmit={(e) => { e.preventDefault(); if (passwordMatch()) { register(); } }}>
                      <div className="stepTitle"><span>Your Information</span>{ getStepText() }</div>
                      <Input onChange={(e) => { setPassword(e.target.value); }} required type="password" icon={keyIcon} label="Password" placeholder="Enter your password" />
                      <Input onChange={(e) => { setRepeat(e.target.value); }} required type="password" icon={keyIcon} label="Repeated Password" placeholder="Enter your password again" />
                      <div className="policy">
                        <p>By clicking Finish, you agree to our <Link to="/terms-of-service">Terms of Service</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.</p>
                      </div>
                      <div className="actions two">
                        <button type="button" onClick={() => { setStep(step - 1); }}>Previous</button>
                        <Button isLoading={isLoading} type="submit">Finish</Button>
                      </div>
                      <div className="register">Already have an account? <Link to="/login">Log in</Link></div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KennelClub;
